import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import {
  loungeQueryOptions,
  useCreateInvite,
  useLoungeInvites,
  useRevokeInvite,
} from '@/features/lounges/api'
import { errorMessage } from '@/lib/graphql'

import './invite.css'

export const Route = createFileRoute('/_app/l/$loungeSlug/invite')({
  loader: async ({ context: { queryClient }, params: { loungeSlug } }) => {
    const { lounge } = await queryClient.ensureQueryData(loungeQueryOptions(loungeSlug))
    return { loungeId: lounge?.id ?? null }
  },
  component: InviteModal,
})

function InviteModal() {
  const { loungeSlug } = Route.useParams()
  const { loungeId } = Route.useLoaderData()
  const navigate = useNavigate()

  const close = () => void navigate({ to: '/l/$loungeSlug', params: { loungeSlug } })

  if (!loungeId) return null

  return (
    <Dialog
      open
      onClose={close}
      title="Invite people"
      description="Anyone with the link can join. They do not need an account first — registering through the link brings them straight in."
    >
      <InviteBody loungeId={loungeId} onDone={close} />
    </Dialog>
  )
}

/** Options the API always had and the UI never sent. */
const EXPIRY = [
  { id: 'never', label: 'Never expires', hours: null },
  { id: 'day', label: 'One day', hours: 24 },
  { id: 'week', label: 'One week', hours: 24 * 7 },
] as const

const USES = [
  { id: 'any', label: 'Any number of people', max: null },
  { id: 'one', label: 'One person', max: 1 },
  { id: 'five', label: 'Five people', max: 5 },
] as const

function InviteBody({ loungeId, onDone }: { loungeId: string; onDone: () => void }) {
  const { data } = useLoungeInvites(loungeId)
  const createInvite = useCreateInvite(loungeId)
  const revokeInvite = useRevokeInvite(loungeId)

  const [expiry, setExpiry] = useState<(typeof EXPIRY)[number]['id']>('never')
  const [uses, setUses] = useState<(typeof USES)[number]['id']>('any')

  const invites = data?.loungeInvites ?? []
  const live = invites.filter((invite) => invite.isValid)

  const create = () => {
    const hours = EXPIRY.find((option) => option.id === expiry)?.hours ?? null
    const maxUses = USES.find((option) => option.id === uses)?.max ?? null
    createInvite.mutate({
      maxUses,
      // Computed here rather than on the server: the server takes an absolute
      // instant, and "a week from when I pressed the button" is a decision the
      // person pressing it is making.
      expiresAt: hours === null ? null : new Date(Date.now() + hours * 3_600_000).toISOString(),
    })
  }

  return (
    <>
      <div className="dialog-body">
        {live.length === 0 ? (
          <p className="muted">No live invites. Make one and share the link.</p>
        ) : (
          <ul className="invite-list">
            {live.map((invite) => (
              <InviteRow
                key={invite.id}
                code={invite.code}
                uses={invite.uses}
                maxUses={invite.maxUses}
                onRevoke={() => revokeInvite.mutate(invite.code)}
                revoking={revokeInvite.isPending}
              />
            ))}
          </ul>
        )}

        <div className="invite-options">
          <label className="field-label" htmlFor="invite-expiry">
            Expires
          </label>
          <select
            id="invite-expiry"
            className="field-input"
            value={expiry}
            onChange={(event) => setExpiry(event.target.value as typeof expiry)}
          >
            {EXPIRY.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>

          <label className="field-label" htmlFor="invite-uses">
            Good for
          </label>
          <select
            id="invite-uses"
            className="field-input"
            value={uses}
            onChange={(event) => setUses(event.target.value as typeof uses)}
          >
            {USES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {createInvite.isError && (
          <p className="form-error" role="alert">
            {errorMessage(createInvite.error, 'That invite could not be created.')}
          </p>
        )}
      </div>

      <div className="dialog-actions">
        <Button onClick={onDone}>Done</Button>
        <Button variant="primary" disabled={createInvite.isPending} onClick={create}>
          {createInvite.isPending ? 'Creating…' : 'New invite link'}
        </Button>
      </div>
    </>
  )
}

/**
 * Whole sentences, chosen by a condition.
 *
 * The rule this milestone adopts: no user-facing string is *built* by
 * concatenation, so extracting them later is mechanical rather than
 * archaeological. "3" and "uses" glued in JSX is the same problem as `a + b`.
 */
function usesLabel(uses: number, maxUses: number | null | undefined): string {
  if (maxUses != null) return `${uses} of ${maxUses} used`
  if (uses === 0) return 'Not used yet'
  if (uses === 1) return 'Used once'
  return `Used ${uses} times`
}

function InviteRow({
  code,
  uses,
  maxUses,
  onRevoke,
  revoking,
}: {
  code: string
  uses: number
  maxUses?: number | null
  onRevoke: () => void
  revoking: boolean
}) {
  const [copied, setCopied] = useState(false)
  const url = `${window.location.origin}/invite/${code}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      // The label reverts on its own; no state to clean up if the dialog closes
      // first, because the component goes with it.
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused (insecure origin, denied permission).
      // Selecting the text is the fallback, and it is already selectable.
      setCopied(false)
    }
  }

  return (
    <li className="invite-row">
      <div className="invite-link">
        <code>{url}</code>
        <p className="invite-meta">{usesLabel(uses, maxUses)}</p>
      </div>

      <div className="invite-actions">
        <Button onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</Button>
        <Button variant="quiet" onClick={onRevoke} disabled={revoking}>
          Revoke
        </Button>
      </div>
    </li>
  )
}
